// Relationships(회사·담당자 통합) — 프런트 서비스.
// 목록/회사 CRUD = /api/sales/companies, 담당자 CRUD = /api/sales/contacts (contactsService 재사용).
import { apiFetch, ApiError, ApiHtmlResponseError } from "../../../lib/apiFetch";
import type { CrmContact } from "../contactsService";

export type CompanyRole = "customer" | "partner" | "prospect" | "other";
/** 회사 추가 시 고르는 구분. 기타 = 고객도 파트너도 아닌 회사(협회·기관·외주사) — crm_accounts.account_type='other'. */
export type CompanyKind = "customer" | "partner" | "other";

/** 통일 양식 — 파트너사 기본정보 기준 + 전화번호. 서버 lib/company-profile.js 의 PROFILE_KEYS 와 같다. */
export type CompanyProfile = {
  name_ko: string;
  name_en: string;
  biz_num: string;
  website: string;
  postal_code: string;
  address: string;
  address_en: string;
  main_technologies: string[];
  company_phone: string;
};

export type ContactBrief = {
  id?: string;
  name: string | null;
  title: string | null;
  department?: string | null;
  email: string | null;
  phone: string | null;
};

export type CompanyRow = {
  /** a:<account_id> | p:<partner_id> | l:<lead_id> — 화면 키 */
  id: string;
  key: string;
  name: string | null;
  roles: CompanyRole[];
  /** false = 비활성(숨김). 목록은 활성·비활성을 다 받고 화면의 상태 필터가 가른다. */
  is_active: boolean;
  /** 출처 — project(수주) · opportunity(수주 전) · vault/external(리드뿐인 잠재고객) · null */
  source_group?: "project" | "opportunity" | "vault" | "external" | null;
  account_id: string | null;
  partner_id: string | null;
  lead_id: string | null;
  industry: string | null;
  profile: CompanyProfile;
  logo_url: string | null;
  contract_type: string | null;
  /** 파트너 서명본(대표문서/MOU) 첨부 여부. 파트너가 아니면 null. */
  partner_has_signed_doc?: boolean | null;
  /** 파트너 서명본 경로(private 버킷) — 업로드 시각 조회용. */
  partner_signed_doc_path?: string | null;
  contact_count: number;
  lead_count: number;
  primary_contact: ContactBrief | null;
  lead_contact: ContactBrief | null;
};

/** is_active — 담당자 활성(= crm_contacts.is_current !== false). */
export type CompanyContact = CrmContact & { company_id: string | null; is_active?: boolean };

export type CompaniesResponse = {
  companies: CompanyRow[];
  contacts: CompanyContact[];
  counts: Record<string, number>;
  schema_pending: boolean;
};

export const EMPTY_PROFILE: CompanyProfile = {
  name_ko: "", name_en: "", biz_num: "", website: "", postal_code: "",
  address: "", address_en: "", main_technologies: [], company_phone: "",
};

export const ROLE_LABEL: Record<CompanyRole, string> = { customer: "고객사", partner: "파트너사", prospect: "잠재고객", other: "기타" };
/** ROLE_LABEL 의 영문 — 화면 언어가 en 일 때. */
export const ROLE_LABEL_EN: Record<CompanyRole, string> = { customer: "Customer", partner: "Partner", prospect: "Prospect", other: "Other" };
export function roleLabel(role: CompanyRole, language: "ko" | "en" = "ko"): string {
  return (language === "en" ? ROLE_LABEL_EN : ROLE_LABEL)[role] || role;
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  try {
    const data = await apiFetch<T>(path, {
      headers: init?.body ? { "Content-Type": "application/json" } : undefined,
      ...init,
    });
    const inlineError = (data as unknown as { error?: string } | undefined)?.error;
    if (inlineError) throw new Error(inlineError);
    return (data ?? {}) as T;
  } catch (err) {
    if (err instanceof ApiHtmlResponseError) throw err;
    if (err instanceof ApiError) {
      const body = err.body as { error?: string; duplicate_of?: { id: string; name: string } } | null;
      const e = new Error(body?.error || `요청 실패 (${err.status})`) as Error & { status?: number; duplicateOf?: { id: string; name: string } };
      e.status = err.status;
      if (body?.duplicate_of) e.duplicateOf = body.duplicate_of;
      throw e;
    }
    throw err;
  }
}

export async function fetchCompanies(): Promise<CompaniesResponse> {
  const data = await api<Partial<CompaniesResponse>>(`/api/sales/companies`);
  return {
    companies: data.companies || [],
    contacts: data.contacts || [],
    counts: data.counts || {},
    schema_pending: !!data.schema_pending,
  };
}

export type NewContactDraft = {
  name: string;
  title: string;
  department: string;
  phone: string;
  email: string;
  is_primary: boolean;
};

export async function createCompany(input: {
  role: CompanyKind;
  profile: CompanyProfile;
  logo_url?: string | null;
  force?: boolean;
  contacts?: NewContactDraft[];
  attach_contact_ids?: string[];
  primary_contact_id?: string | null;
}): Promise<{ account_id: string | null; partner_id: string | null; schema_pending: boolean }> {
  return api(`/api/sales/companies`, { method: "POST", body: JSON.stringify(input) });
}

function orgQuery(c: { account_id: string | null; partner_id: string | null }) {
  const q = new URLSearchParams();
  if (c.account_id) q.set("account_id", c.account_id);
  if (c.partner_id) q.set("partner_id", c.partner_id);
  return q.toString();
}

/** 부분 profile — 보낸 칸만 저장된다(서버 sanitizeProfile 이 없는 키는 건드리지 않는다). */
export async function updateCompany(
  c: { account_id: string | null; partner_id: string | null },
  profile: Partial<CompanyProfile>,
  /** 로고는 바뀌었을 때만 보낸다 — undefined = 그대로, null = 삭제. */
  logoUrl?: string | null,
): Promise<{ schema_pending: boolean }> {
  const body: Record<string, unknown> = { profile };
  if (logoUrl !== undefined) body.logo_url = logoUrl;
  return api(`/api/sales/companies?${orgQuery(c)}`, { method: "PUT", body: JSON.stringify(body) });
}

/**
 * 로고 업로드 — 파트너사 로고와 같은 경로(공개 버킷 partner-logos, 서명 업로드 → 공개 URL).
 * 저장은 호출측이 logo_url 로 한다.
 */
export async function uploadCompanyLogo(file: File): Promise<string> {
  const signRes = await fetch("/api/storage/sign-upload", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ bucket: "partner-logos", filename: file.name, fileSize: file.size }),
  });
  const sign = await signRes.json().catch(() => ({}));
  if (!signRes.ok || !sign.ok) throw new Error(sign.error || "로고 업로드 URL 발급 실패");
  const putRes = await fetch(sign.signedUrl, { method: "PUT", headers: { "Content-Type": file.type || "application/octet-stream" }, body: file });
  if (!putRes.ok) throw new Error(`로고 업로드 실패 (${putRes.status})`);
  return String(sign.publicUrl || sign.path);
}

/** 활성화·비활성화(겸용 회사는 양쪽 행). 비활성 = 목록에서 숨김, 데이터·연결은 그대로. */
export async function setCompanyActive(c: { account_id: string | null; partner_id: string | null }, active: boolean): Promise<void> {
  await api(`/api/sales/companies?${orgQuery(c)}`, { method: "PUT", body: JSON.stringify({ profile: {}, is_active: active }) });
}

/**
 * 완전 삭제 — 회사 행과 그 회사 담당자를 지운다(되돌릴 수 없음).
 * 고객사·기타에 프로젝트·구독이 걸려 있으면 서버가 409 로 막는다(메시지에 건수) — 그때는 비활성화.
 */
export async function deleteCompany(c: { account_id: string | null; partner_id: string | null }): Promise<void> {
  await api(`/api/sales/companies?${orgQuery(c)}&hard=1`, { method: "DELETE" });
}

/** 담당자 활성화·비활성화. 비활성으로 돌리면 기본 연락처에서도 내려간다. */
export async function setContactActive(id: string, active: boolean): Promise<void> {
  await api(`/api/sales/contacts`, { method: "POST", body: JSON.stringify({ op: "set-active", id, active }) });
}

export async function setPrimaryContact(id: string): Promise<void> {
  await api(`/api/sales/contacts`, { method: "POST", body: JSON.stringify({ op: "set-primary", id }) });
}

/** 회사 → 담당자 소속 필드. 겸용 회사는 고객사 쪽이 기본(side 로 바꿀 수 있다). */
export function contactOrgFields(c: { account_id: string | null; partner_id: string | null }, side?: "customer" | "partner") {
  const useSide = side || (c.account_id ? "customer" : "partner");
  return useSide === "partner" && c.partner_id
    ? { contact_type: "partner", partner_id: c.partner_id, account_id: null }
    : { contact_type: "customer", account_id: c.account_id, partner_id: null };
}

/** LIKE 검색 — 공백·대소문자 무시 부분 일치. */
export function likeMatch(q: string, ...values: Array<string | null | undefined>): boolean {
  const needle = q.trim().toLowerCase().replace(/\s+/g, "");
  if (!needle) return true;
  return values.some((v) => String(v ?? "").toLowerCase().replace(/\s+/g, "").includes(needle));
}
